// src/components/programs/EndurancePlans/EnduranceBeginnerPlans.jsx
import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { saveAs } from "file-saver"; // npm i file-saver
import SamplePDF from "../../../assets/Programs/Endurance Plans/Beginner/All_beginner_endurance_plans.pdf";

export default function EnduranceBeginnerPlans() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const totalWeeks = parseInt(params.get("weeks")) || 6;
  const [selectedWeek, setSelectedWeek] = useState(1);

  const weekPlan = {
    1: [
      {
        session: "Session 1",
        exercises: ["Jumping Jacks", "Push-ups", "Squats"],
        sets: [3, 3, 3],
        reps: ["30 sec", 10, 15],
        optional: ["Burpees 2x15", "Plank 2x30s", "Wall Sit 2x30s"],
      },
      {
        session: "Session 2",
        exercises: ["High Knees", "Dumbbell Rows", "Lunges"],
        sets: [3, 3, 3],
        reps: ["30 sec", 12, 12],
        optional: ["Mountain Climbers 2x20", "Resistance Band Pulls 2x12", "Step-ups 2x12"],
      },
    ],
    2: [
      {
        session: "Session 1",
        exercises: ["Jump Rope", "Plank", "Lunges"],
        sets: [3, 3, 3],
        reps: ["1 min", "30 sec", "12"],
        optional: ["Skater Jumps 2x20", "Side Plank 2x20s", "Step-ups 2x12"],
      },
      {
        session: "Session 2",
        exercises: ["Squats", "Push-ups", "High Knees"],
        sets: [3, 3, 3],
        reps: ["15", "10", "30 sec"],
        optional: ["Burpees 2x15", "Plank 2x30s", "Mountain Climbers 2x20"],
      },
    ],
  };

  const handleBack = () => {
    navigate(-1);
    setTimeout(() => {
      const el = document.getElementById("sports-section");
      if (el) {
        const navbarHeight = 80;
        const topPos = el.getBoundingClientRect().top + window.pageYOffset - navbarHeight - 20;
        window.scrollTo({ top: topPos, behavior: "smooth" });
      }
    }, 50);
  };

  const downloadPDF = () => {
    saveAs(SamplePDF, "Endurance-Plan.pdf");
  };

  return (
    <section className="w-full px-6 md:px-12 pt-24 pb-20 flex flex-col items-center gap-12 min-h-screen">
      
      {/* Back Button */}
      <motion.button
        onClick={handleBack}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="self-start px-4 py-2 bg-gray-800 text-white rounded-xl font-semibold hover:bg-gray-700 transition"
      >
        ← Back to Sports Options
      </motion.button>

      {/* Table & Week Buttons Wrapper */}
      <div className="w-full max-w-[90%] bg-black/30 p-8 rounded-2xl flex flex-col gap-8 shadow-lg">
        
        {/* Page Heading */}
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-white text-4xl md:text-5xl font-bold text-center md:text-left"
        >
          Beginner Sports Plan ({totalWeeks} Weeks)
        </motion.h2>

        {/* Week Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex gap-3 flex-wrap justify-center md:justify-start"
        >
          {Array.from({ length: totalWeeks }, (_, i) => (
            <motion.button
              key={i}
              onClick={() => setSelectedWeek(i + 1)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-4 py-2 rounded-xl font-semibold transition ${
                selectedWeek === i + 1
                  ? "bg-neonTurquoise text-black"
                  : "bg-gray-800 text-white"
              }`}
            >
              Week-{i + 1}
            </motion.button>
          ))}
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="overflow-x-auto w-full flex justify-center"
        >
          <table className="w-full text-white border border-gray-700 shadow-lg rounded-xl overflow-hidden">
            <thead>
              <tr className="bg-gray-800">
                <th className="p-3">Session</th>
                <th className="p-3">Exercises</th>
                <th className="p-3">Sets</th>
                <th className="p-3">Reps</th>
                <th className="p-3">Optional Exercises</th>
              </tr>
            </thead>
            <tbody>
              {(weekPlan[selectedWeek] || []).map((s, idx) => (
                <tr key={idx} className="border-t border-gray-700 hover:bg-gray-900 transition">
                  <td className="p-3">{s.session}</td>
                  <td className="p-3">{s.exercises.join(", ")}</td>
                  <td className="p-3">{s.sets.join(", ")}</td>
                  <td className="p-3">{s.reps.join(", ")}</td>
                  <td className="p-3">{s.optional.join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          className="flex gap-6 mt-6 flex-wrap justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <button
            onClick={downloadPDF}
            className="px-6 py-3 bg-neonTurquoise text-black font-bold rounded-xl shadow-lg hover:bg-neonTurquoise/80 hover:scale-105 transition"
          >
            Download Free PDF
          </button>
          <Link
            to="/apply-now"
            className="px-6 py-3 bg-neonTurquoise text-black font-bold rounded-xl shadow-lg hover:bg-neonTurquoise/80 hover:scale-105 transition"
          >
            Apply Now
          </Link>
        </motion.div>

      </div>
    </section>
  );
}
